import { useState, FormEvent, FC } from "react";
import { useAuthContext } from "./AuthContext";
import type { LoginResponse } from "../Components/interface";

const API_BASE = process.env.REACT_APP_AUTH_API_URL;

const LoginModal: FC = () => {
  const { isModalOpen, closeLoginModal, onLoginSuccess } = useAuthContext();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isModalOpen) return null;

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!username || !password) {
      setError("Enter your credentials to continue");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const msg =
          res.status === 401
            ? "Invalid username or password"
            : "Authentication failed. Please try again.";
        setError(msg);
        return;
      }

      const { token }: LoginResponse = await res.json();
      console.log("Login successful, received token:", token);
      onLoginSuccess(token);
    } catch {
      setError("Connection error. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-overlay"
      role="dialog"
      aria-modal
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.button !== 0) return;
        closeLoginModal();
      }}
    >
      <div className="auth-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: "0 0 20px", fontSize: 20 }}>AI Access Required</h2>

        <input
          type="text"
          placeholder="Email"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />

        <div className="auth-reviewer-note">
          <div className="auth-reviewer-title">Reviewer Access</div>

          <div className="auth-reviewer-text">
            Use my email address as the username and the last 12 digits of my
            phone number as the password.
          </div>
        </div>

        {error && (
          <div className="auth-error">
            <span className="auth-error-icon">!</span>

            <span className="auth-error-text">{error}</span>
          </div>
        )}

        <button
          className="auth-primary-btn"
          onClick={() => handleSubmit()}
          disabled={loading}
        >
          {loading ? (
            <div className="auth-loading">
              <div className="auth-spinner" />
              <span>Connecting...</span>
            </div>
          ) : (
            "Login"
          )}
        </button>
      </div>
    </div>
  );
};

export default LoginModal;
